import { useEffect, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatDateTime } from '../../../shared/format.js';
import { useReceptionForm } from '../hooks/useReceptionForm.js';

const GENDER_LABEL = { male: 'ذكر', female: 'أنثى' };

// نافذة اختيار أطفال ولي الأمر العائد: تظهر تلقائياً عندما يكون لديه أكثر من
// طفل مسجّل، ويمكن فتحها يدوياً من شريط "زبون عائد". الاختيار متعدد —
// إدخال الجميع أو بعضهم، والأطفال المحددون يدخلون خطوة الأطفال بمعرفاتهم
// الحقيقية فلا تُنشأ لهم سجلات مكررة عند التأكيد.
export function ChildSelectModal() {
  const open = useReceptionForm((s) => s.childPickerOpen);
  const existingChildren = useReceptionForm((s) => s.existingChildren);
  const children = useReceptionForm((s) => s.children);
  const importExistingChildren = useReceptionForm((s) => s.importExistingChildren);
  const setChildPickerOpen = useReceptionForm((s) => s.setChildPickerOpen);

  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    // افتراضياً: تحديد كل الأطفال غير المُدخلين بعد (عدا من هو داخل القاعة الآن).
    const alreadyIn = new Set(children.filter((c) => c.childId).map((c) => String(c.childId)));
    setSelected(
      new Set(
        existingChildren
          .filter((c) => !c.hasActiveSession)
          .map((c) => String(c._id || c.id))
          .filter((id) => !alreadyIn.has(id))
      )
    );
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const alreadyIn = new Set(children.filter((c) => c.childId).map((c) => String(c.childId)));
  const allIds = existingChildren
    .filter((c) => !c.hasActiveSession)
    .map((c) => String(c._id || c.id))
    .filter((id) => !alreadyIn.has(id));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  const toggle = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id);
      else nx.add(id);
      return nx;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allIds));
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center"
      style={{ background: 'rgba(0,0,0,.45)' }}
      onClick={() => setChildPickerOpen(false)}
    >
      <div className="card p-5 w-[480px] max-w-[94vw]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Icon name="reception" size={18} />
          <h3 className="text-[15px] font-bold flex-1">أطفال مسجّلون لهذا الرقم</h3>
          <Pill variant="orange">{existingChildren.length}</Pill>
        </div>
        <div className="text-xs text-ink-3 mb-3">
          اختر الأطفال الذين حضروا اليوم — سيُدخلون مباشرة دون إعادة تسجيل بياناتهم.
        </div>

        <label className="flex items-center gap-2 text-xs font-semibold text-ink-2 mb-2 cursor-pointer select-none">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={allIds.length === 0} />
          تحديد الكل
        </label>

        <div className="space-y-2 max-h-[320px] overflow-y-auto pe-1">
          {existingChildren.map((c) => {
            const id = String(c._id || c.id);
            const isInside = !!c.hasActiveSession;
            const isIn = alreadyIn.has(id) || isInside;
            return (
              <label
                key={id}
                className={`flex items-center gap-3 p-2.5 border rounded-[10px] select-none ${
                  isIn
                    ? 'border-line bg-paper-2/50 opacity-60'
                    : selected.has(id)
                      ? 'border-orange bg-orange-soft cursor-pointer'
                      : 'border-line cursor-pointer hover:bg-paper-2'
                }`}
              >
                <input
                  type="checkbox"
                  checked={(alreadyIn.has(id) || selected.has(id)) && !isInside}
                  disabled={isIn}
                  onChange={() => toggle(id)}
                />
                {c.photoUrl ? (
                  <img
                    src={c.photoUrl}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover border border-line"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-paper-2 grid place-items-center text-ink-3">
                    <Icon name="reception" size={16} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold truncate">{c.name}</div>
                  <div className="text-[11px] text-ink-3">
                    {c.age != null ? `${c.age} سنة` : ''} {c.gender ? `— ${GENDER_LABEL[c.gender] || ''}` : ''}
                    {c.lastVisitAt ? ` — آخر زيارة ${formatDateTime(c.lastVisitAt)}` : ''}
                  </div>
                </div>
                {isInside ? (
                  <Pill variant="danger">بالداخل الآن</Pill>
                ) : alreadyIn.has(id) ? (
                  <Pill variant="orange">مُدخل</Pill>
                ) : null}
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2 mt-4">
          <button type="button" className="btn" onClick={() => setChildPickerOpen(false)}>
            إدخال يدوي
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.size === 0}
            style={selected.size === 0 ? { opacity: 0.6 } : undefined}
            onClick={() => importExistingChildren([...selected])}
          >
            <Icon name="check" size={14} /> إدخال المحدد ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}
